<script setup lang="ts">

import { computed, defineProps } from 'vue'
import { usePlanetStore } from '@/stores/planetStore.ts'
import StandardCard from '@/components/Elements/Cards/StandardCard.vue'
import NamedNumbers from '@/components/Elements/Generic/NamedNumbers.vue'
import GenericIconWithColoredNumber from '@/components/Elements/Images/Icons/GenericIconWithColoredNumber.vue'
import SimpleProgressBar from '@/components/Elements/ProgressBar/SimpleProgressBar.vue'
import { useI18n } from 'vue-i18n'

const { t } = useI18n()

/**
 * Props
 */
const props = defineProps({
  planetId: {
    type: Number,
    required: true
  }
});

/**
 * Stores / models
 */
const planetStore = usePlanetStore()


/**
 * Computed
 */
const planetData = computed(() => {
  return planetStore.findPlanetById(props.planetId)!
})

const correctedPopulationGrowthRequired = computed(() => {
  const population = planetData.value.population
  const isRingworld = Number(planetData.value.type) === 8
  
  // Cap non-ringworld planets to population 30
  const cappedPopulation = isRingworld ? population : Math.min(population, 30)
  
  // (population^2.0 ÷ 3) * 1.5 + 1
  return Math.floor(((Math.pow(cappedPopulation, 2.0) / 3) * 1.5)) + 1
})

const percentage = computed(() => {
  return (planetData.value.populationGrowthProcess / correctedPopulationGrowthRequired.value) * 100
})

const etaText = computed(() => {
  const amount = Math.ceil((correctedPopulationGrowthRequired.value - planetData.value.populationGrowthProcess) / planetData.value.populationGrowthPerTurn)
  if(amount <= 0) return t('planets.population.week')
  return t('planets.population.weeks', { count: amount })
})

const isAtPopulationCap = computed(() => {
  const isRingworld = Number(planetData.value.type) === 8
  return !isRingworld && planetData.value.population >= 30
})


</script>

<template>
<StandardCard v-if="isAtPopulationCap" :title="t('planets.population.limitReached.title')" top-padding="2">
    {{ t('planets.population.limitReached.description1') }} <span class="text-red-500">{{ t('planets.population.limitReached.maxPopulation') }}</span> {{ t('planets.population.limitReached.description2') }}
    {{ t('planets.population.limitReached.solution1') }} <span class="text-slate-400">{{ t('planets.population.limitReached.convertToRingworld') }}</span>.
    {{ t('planets.population.limitReached.solution2') }}
</StandardCard>
<StandardCard v-else :title="t('planets.population.title')" top-padding="2" :help="t('planets.population.help')">

  <div class="flex justify-between pb-4">
    <NamedNumbers :text="t('planets.population.populationLabel')">
      <GenericIconWithColoredNumber :amount="planetData.population" type="person"/>
    </NamedNumbers>

    <NamedNumbers :text="t('planets.population.foodSurplus')">
      <GenericIconWithColoredNumber :amount="planetData.populationGrowthPerTurn" type="food"/>
    </NamedNumbers>
  </div>
  <SimpleProgressBar :percentage="percentage" :text="etaText" color="blue" :height="6"/>
  <div class="flex justify-between pt-4">
    <NamedNumbers :text="t('planets.population.stored')">
      <GenericIconWithColoredNumber :amount="planetData.populationGrowthProcess" type="food"/>
    </NamedNumbers>

    <NamedNumbers :text="t('planets.population.required')">
      <GenericIconWithColoredNumber :amount="correctedPopulationGrowthRequired" type="food"/>
    </NamedNumbers>
  </div>
</StandardCard>


</template>

<style scoped lang="scss">

</style>
